import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { cloneElement, isValidElement, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFeatures } from '@atc/features';
import { Form } from 'reaxl-forms';
import { financingDisclaimers } from 'axl-config';
import EmailContext from '../EmailContext';
import EmailFormBody from '../EmailFormBody';
import EmailFormFooter from '../EmailFormFooter';
import EmailFormHeader from '../EmailFormHeader';
import EmailStaticContent from '../EmailStaticContent';
import HomeServicesOptIn from '../HomeServicesOptIn';
import OneClickButton from '../OneClickButton';
import VehicleOptions from '../VehicleOptions';
import WalletSnapshot from '../WalletSnapshot/WalletSnapshot';

/**
 *  EmailForm is the component includes title, header, body, home services checkboxes, footer and quick save do-not-show checkbox
 */
function EmailForm(_ref) {
  var _HomeServicesOptIn, _VehicleOptions;
  let {
    buttonStyle = 'primary',
    ctaContext,
    formattedFilterValues = [],
    handleFormSubmit = () => {},
    handleOneClickEmailSendClick = () => {},
    isButtonDisabled = false,
    isConfirmation = false,
    isEditEmailModal = false,
    inquiryProps = {
      inquirySelection: 'confirmAvailability',
      handleInquirySelection: () => {}
    },
    ndsProps = {},
    phoneClickHandler = () => {},
    serverError,
    submitState = '',
    showAlert,
    showOneClickSuccess,
    setOneClickEmailSent = () => {},
    userProfile = {},
    userDuck,
    isAccelerateScheduleTestDrive = false,
    hideTestDrive = false,
    nextSteps,
    oneClickChecked = false,
    setOneClickChecked = () => {},
    additionalInventoryInfo = {},
    alternateFormContent,
    handleListingSelect,
    handleEmailInputInteraction,
    handleEditSettingsClick,
    messageOptIn,
    setMessageOptIn,
    isLoggedIn,
    isIncentivesSheet
  } = _ref;
  const {
    one_click_lead_vdp: [enableOneClickVDP],
    scripts: [enableScripts, {
      cadNitrous: enableCadNitrous
    }]
  } = useFeatures(['one_click_lead_vdp', 'scripts']);
  const {
    listing: {
      id: listingId
    } = {},
    uiContext
  } = useContext(EmailContext);
  const [showSMSOptIn, setShowSMSOptIn] = useState(false);
  const leadProfile = useSelector(state => userDuck?.selectors?.getLeadProfile(state)) || {};
  const {
    email,
    firstName,
    lastName,
    isOneClickOptedIn
  } = leadProfile;
  const isUserProfileIncomplete = (!email || !firstName || !lastName) && isOneClickOptedIn;
  if (isUserProfileIncomplete) {
    setOneClickEmailSent(false);
  }
  const showVehicleOptions = uiContext === 'quote' && !!formattedFilterValues?.length;
  const {
    walletSummary = [],
    walletEstimates = {},
    dealType,
    walletOptIn = false,
    preferredLenderSpecified,
    creditApplications = [],
    payments = {}
  } = ndsProps;
  const showEmailForm = !(inquiryProps.inquirySelection === 'scheduleTestDrive' && isAccelerateScheduleTestDrive);
  const showOneClick = isOneClickOptedIn && (uiContext === 'default' || uiContext === 'multi') && enableOneClickVDP && !isConfirmation;
  const handleShowEmailForm = () => {
    inquiryProps.handleInquirySelection(null, 'confirmAvailability', false);
  };

  // When the sms checkbox gets crendered/derendered the user's opted in status needs to mirror the change.
  const enableSMSOptInDisplay = useCallback(enabled => {
    setShowSMSOptIn(enabled);
    setMessageOptIn(enabled);
  }, [setMessageOptIn, setShowSMSOptIn]);
  const inPageInquiryProps = {
    ...inquiryProps,
    creditApplications,
    hideTestDrive
  };
  let nitrousElement;
  if (enableScripts && enableCadNitrous) {
    if (ctaContext === 'calculatorOfferSummary') {
      nitrousElement = 'Send Offer Form';
    } else if (inquiryProps?.inquirySelection === 'confirmAvailability') {
      nitrousElement = 'Contact Dealer Form';
    }
  }
  const emailForm = function (key) {
    if (key === void 0) {
      key = 'eml-form';
    }
    return /*#__PURE__*/_jsx(Form, {
      onValidSubmit: handleFormSubmit,
      "data-cmp": "emailForm",
      "data-nitrous-element-name": nitrousElement
    }, key, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(EmailFormBody, {
      ctaContext: ctaContext,
      inquiryProps: {
        ...inPageInquiryProps
      },
      enableSMSOptInDisplay: enableSMSOptInDisplay,
      userDuck: userDuck,
      isEditEmailModal: isEditEmailModal,
      isOneClickChecked: oneClickChecked,
      setOneClickChecked: setOneClickChecked,
      showInquiriesOnly: !showEmailForm,
      userProfile: userProfile,
      handleEmailInputInteraction: handleEmailInputInteraction,
      leadProfile: leadProfile,
      isLoggedIn: isLoggedIn
    }), showEmailForm && /*#__PURE__*/React.createElement(React.Fragment, null, uiContext !== 'testDrive' && (_HomeServicesOptIn || (_HomeServicesOptIn = /*#__PURE__*/_jsx(HomeServicesOptIn, {
      preSelectedService: ctaContext
    }))), showVehicleOptions && (_VehicleOptions || (_VehicleOptions = /*#__PURE__*/_jsx(VehicleOptions, {
      filterValues: formattedFilterValues
    }))), /*#__PURE__*/_jsx(EmailFormFooter, {
      animatedButton: ['vehicleHistoryReport', 'testDrive'].includes(uiContext),
      buttonStyle: buttonStyle,
      messageOptIn: messageOptIn,
      ctaContext: ctaContext,
      inquiryProps: {
        ...inquiryProps
      },
      isButtonDisabled: isButtonDisabled,
      isEditEmailModal: isEditEmailModal,
      isIncentivesSheet: isIncentivesSheet,
      isOneClickEdit: isOneClickOptedIn,
      walletOptIn: walletOptIn,
      showFinanceDisclaimer: dealType === 'finance' && ['calculator', 'calculatorOfferSummary'].includes(uiContext),
      uiContext: uiContext,
      setMessageOptIn: showSMSOptIn && setMessageOptIn,
      submitState: submitState
    }))));
  };
  const emailWithWallet = () => {
    const summaryContent = walletSummary;
    const estimateSnapshotDetails = {};
    const isCashDeal = dealType === 'cash';
    const isLeaseDeal = dealType === 'lease';
    const hasInvalidLeaseDeal = isLeaseDeal && payments?.payment === 'N/A';
    if (!isCashDeal) {
      estimateSnapshotDetails.contents = walletEstimates.email;
    }
    if (isLeaseDeal) {
      estimateSnapshotDetails.title = 'Leasing Estimates';
    }
    if (!isLeaseDeal) {
      estimateSnapshotDetails.title = 'Financing Estimates';
      estimateSnapshotDetails.disclaimer = preferredLenderSpecified ? '' : financingDisclaimers.defaultLenderDisclaimer;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "row"
    }, void 0, /*#__PURE__*/_jsx("div", {
      className: "col-xs-12 col-sm-5"
    }, void 0, /*#__PURE__*/_jsx(WalletSnapshot, {
      title: "Offer Summary",
      contents: summaryContent
    }), !isCashDeal && !hasInvalidLeaseDeal && /*#__PURE__*/React.createElement(WalletSnapshot, estimateSnapshotDetails)), /*#__PURE__*/_jsx("div", {
      className: "col-xs-12 col-sm-6 col-sm-offset-1"
    }, void 0, emailForm('nds-eml-form')));
  };
  const renderEmailForm = () => {
    if (uiContext === 'calculator') {
      return emailWithWallet();
    }
    if (uiContext === 'calculatorOfferSummary') {
      return emailForm('nds-eml-form');
    }
    return emailForm();
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(EmailFormHeader, {
    isConfirmation: isConfirmation,
    isEditEmailModal: isEditEmailModal,
    isUserProfileIncomplete: isUserProfileIncomplete,
    showAlert: showAlert && !showOneClick,
    serverError: serverError,
    isOneClickOptedIn: isOneClickOptedIn !== undefined ? isOneClickOptedIn : oneClickChecked,
    phoneClickHandler: phoneClickHandler,
    nextSteps: nextSteps,
    additionalInventoryInfo: additionalInventoryInfo,
    handleListingSelect: handleListingSelect,
    isIncentivesSheet: isIncentivesSheet
  }), !isConfirmation && !showOneClick && renderEmailForm(), isConfirmation && !!additionalInventoryInfo?.listings?.length && emailForm(), showOneClick && /*#__PURE__*/_jsx(OneClickButton, {
    listingId: listingId,
    handleEmailSendClick: handleOneClickEmailSendClick,
    handleEditSettingsClick: handleEditSettingsClick,
    isModifySettingsIcon: false,
    oneClickEmailSent: showOneClickSuccess
  }), !showEmailForm && /*#__PURE__*/isValidElement(alternateFormContent) && /*#__PURE__*/cloneElement(alternateFormContent, {
    listedPrice: payments.salePrice,
    paymentsInfo: payments,
    onClick: handleShowEmailForm
  }));
}
export default EmailForm;